import React, { useState, useMemo } from 'react';
import { DataTable, DataTableRowClickEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { PaymentMethodIcon } from './componenets/payment-method-icon';
import { PaymentDetailsTooltip } from './componenets/payment-details-tooltip';
import { PaymentSummary } from './componenets/payment-summary';
import { InstallmentSummary } from './componenets/installment-summary';
import {
  calculateInstallmentSummaries,
  calculateTotalInstallmentAmount,
} from './utils/calculate-installment-summaries';
import dateUtils from '../../../../../../utils/date-format';
import { FileDown, Undo2 } from 'lucide-react';
import {
  PaymentReferenceType,
  UserPayment,
  UserPaymentWithItemPrice,
} from '../../../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';
import { UserSummaryType } from '../../../../../../api/types/user/user-summary.type';
import { RefundDialog } from './dialogs/refund-dialog';
import { InstallmentWithPlanItemsDetails } from '../../../../../../api/types/payments/installments/entities';
import { InstallmentBankTransferWithPlanItemsDetails } from '../../../../../../api/types/students/user-details-student';

export interface PaymentServiceHistoryListProps {
  payments: UserPaymentWithItemPrice[];
  totalPrice: number;
  user: UserSummaryType;
  allocationId: number;
  creditCardInstallmentPlans?: InstallmentWithPlanItemsDetails[];
  bankTransferInstallmentPlans?: InstallmentBankTransferWithPlanItemsDetails[];
}

export const PaymentServiceHistoryList: React.FC<
  PaymentServiceHistoryListProps
> = ({
  payments,
  totalPrice,
  user,
  allocationId,
  creditCardInstallmentPlans = [],
  bankTransferInstallmentPlans = [],
}) => {
  const [expandedRows, setExpandedRows] = React.useState<UserPayment[]>([]);
  const [selectedPaymentForRefund, setSelectedPaymentForRefund] =
    useState<UserPayment | null>(null);
  const [isRefundDialogVisible, setIsRefundDialogVisible] = useState(false);

  // Calculate installment plan summaries for this allocation
  const installmentSummaries = useMemo(
    () =>
      calculateInstallmentSummaries({
        allocationId,
        creditCardInstallmentPlans,
        bankTransferInstallmentPlans,
      }),
    [allocationId, creditCardInstallmentPlans, bankTransferInstallmentPlans],
  );

  const totalInstallmentAmount = useMemo(
    () => calculateTotalInstallmentAmount(installmentSummaries),
    [installmentSummaries],
  );

  const onRowClick = React.useCallback((e: DataTableRowClickEvent) => {
    const clickedPayment = e.data as UserPayment;
    setExpandedRows((prev) =>
      prev.some((p) => p.id === clickedPayment.id)
        ? prev.filter((p) => p.id !== clickedPayment.id)
        : [...prev, clickedPayment],
    );
  }, []);

  const handleRefundClick = (payment: UserPayment) => {
    setSelectedPaymentForRefund(payment);
    setIsRefundDialogVisible(true);
  };

  const handleRefundDialogClose = () => {
    setIsRefundDialogVisible(false);
    setSelectedPaymentForRefund(null);
  };

  const refundButtonTemplate = (rowData: UserPayment) => {
    // Only show refund button for payments with parentId null
    if (rowData.paymentReferenceType === PaymentReferenceType.REVERSAL) {
      return null;
    }

    return (
      <button
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '0.5rem 1rem',
          border: '1px solid #E5E7EB',
          borderRadius: '0.375rem',
          backgroundColor: '#FEF3C7',
          color: '#92400E',
          fontSize: '0.875rem',
          fontWeight: 500,
          cursor: 'pointer',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleRefundClick(rowData);
        }}
      >
        <Undo2
          style={{
            width: '16px',
            height: '16px',
            marginRight: '0.5rem',
          }}
        />
        החזר כספי
      </button>
    );
  };

  const rowExpansionTemplate = (payment: UserPayment) => (
    <PaymentDetailsTooltip payment={payment} />
  );

  return (
    <div className="payment-service-history">
      <InstallmentSummary
        installmentSummaries={installmentSummaries}
        totalInstallmentAmount={totalInstallmentAmount}
      />
      <PaymentSummary
        payments={payments}
        totalPrice={totalPrice}
        installmentAmount={totalInstallmentAmount}
      />

      <DataTable
        value={payments}
        expandedRows={expandedRows}
        onRowClick={onRowClick}
        rowExpansionTemplate={rowExpansionTemplate}
        style={{
          marginTop: '1rem',
          cursor: 'pointer',
        }}
        className="payment-history-table"
      >
        <Column
          field="id"
          header="מזהה תשלום"
          style={{ width: '100px', textAlign: 'right' }}
        />
        <Column
          field="paymentAmount"
          header="סכום"
          style={{ width: '150px', textAlign: 'right' }}
          body={(rowData) => (
            <span style={{ color: '#00C853', fontWeight: 'bold' }}>
              {'\u202D'}₪{rowData.itemAmount}
              {'\u202C'}
            </span>
          )}
        />
        <Column
          field="paymentDate"
          header="תאריך תשלום"
          style={{ width: '150px', textAlign: 'right' }}
          body={(rowData) => dateUtils.displayFormatDate(rowData.paymentDate)}
        />
        <Column
          field="paymentMethodType"
          header="אמצעי תשלום"
          style={{ width: '200px', textAlign: 'right' }}
          body={(rowData) => (
            <PaymentMethodIcon methodType={rowData.paymentMethodType} />
          )}
        />

        <Column
          field="paymentMethodType"
          header="חשבונית"
          style={{ width: '200px', textAlign: 'right' }}
          body={(rowData: UserPayment) => (
            <button
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '0.5rem 1rem',
                border: '1px solid #E5E7EB',
                borderRadius: '0.375rem',
                backgroundColor: 'white',
                color: '#374151',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
              }}
              onClick={(e) => {
                e.stopPropagation();
                window.open(rowData.invoiceUrl, '_blank');
              }}
            >
              <FileDown
                style={{
                  width: '16px',
                  height: '16px',
                  marginRight: '0.5rem',
                }}
              />
              לחץ לצפייה
            </button>
          )}
        />
        <Column
          field="paymentDetails"
          header="פרטים נוספים"
          style={{ width: '100px', textAlign: 'right' }}
          body={(rowData) => <PaymentDetailsTooltip payment={rowData} />}
        />
        <Column
          header="החזר כספי"
          style={{ width: '150px', textAlign: 'right' }}
          body={refundButtonTemplate}
        />
      </DataTable>

      {selectedPaymentForRefund && (
        <RefundDialog
          visible={isRefundDialogVisible}
          payment={selectedPaymentForRefund}
          user={user}
          onClose={handleRefundDialogClose}
        />
      )}
    </div>
  );
};
